import { useNavigate } from "@tanstack/react-router"
import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

interface Conversation {
  id: string
  status: "active" | "paused" | "completed" | "human_takeover"
  platform: string
  priority: number
  humanTakeover: boolean
  contact?: {
    id: string
    name?: string
    email?: string
    phone?: string
  }
  campaign?: {
    id: string
    name: string
  }
  currentStage?: {
    id: string
    name: string
  }
  messages?: {
    id: string
    content: string
    direction: "inbound" | "outbound"
    sentAt: string
  }[]
  lastActivityAt: string
  createdAt: string
}

const StatusBadge: React.FC<{ status: string; humanTakeover?: boolean }> = ({ status, humanTakeover }) => {
  if (humanTakeover) {
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-300">
        👨‍💻 Human
      </span>
    )
  }

  const colors = {
    active: "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-300",
    paused: "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-300",
    completed: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-300",
    human_takeover: "bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-300",
  }

  return (
    <span
      className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status as keyof typeof colors] || colors.active}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ")}
    </span>
  )
}

const PlatformIcon: React.FC<{ platform: string }> = ({ platform }) => {
  const icons = {
    email: "📧",
    sms: "💬",
    whatsapp: "💚",
    messenger: "💙",
  }

  return <span>{icons[platform as keyof typeof icons] || "💬"}</span>
}

const ConversationInbox: React.FC = () => {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string>("")
  const [platformFilter, setPlatformFilter] = useState<string>("")
  const [searchTerm, setSearchTerm] = useState("")

  // Query conversations with filters
  const {
    data: conversationsData,
    isLoading,
    refetch,
  } = trpc.crm.conversations.list.useQuery({
    status: statusFilter ? (statusFilter as "active" | "human_takeover" | "completed" | "paused") : undefined,
    platform: platformFilter ? (platformFilter as "email" | "sms" | "whatsapp" | "messenger") : undefined,
    search: searchTerm || undefined,
    limit: 50,
    offset: 0,
  })

  // Update conversation status mutation
  const updateStatusMutation = trpc.crm.conversations.updateStatus.useMutation({
    onSuccess: () => {
      void refetch()
    },
  })

  const handleStatusChange = (
    conversationId: string,
    newStatus: "active" | "paused" | "completed" | "human_takeover",
    reason?: string,
  ) => {
    updateStatusMutation.mutate({
      conversationId,
      status: newStatus,
      reason,
    })
  }

  const getLastMessage = (conversation: Conversation) => {
    if (!conversation.messages || conversation.messages.length === 0) {
      return "No messages yet"
    }

    const lastMessage = conversation.messages[0] // Assuming they're sorted by newest first
    const preview =
      lastMessage.content.length > 100 ? lastMessage.content.substring(0, 100) + "..." : lastMessage.content

    const direction = lastMessage.direction === "inbound" ? "→" : "←"
    return `${direction} ${preview}`
  }

  const getPriorityColor = (priority: number) => {
    if (priority >= 8) return "text-destructive dark:text-red-400"
    if (priority >= 6) return "text-orange-600 dark:text-orange-400"
    if (priority >= 4) return "text-yellow-600 dark:text-yellow-400"
    return " dark:text-gray-400"
  }

  return (
    <div className="h-full overflow-auto bg-foregroundx ">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold  dark:text-white">Conversation Inbox</h1>
              <p className="mt-2  dark:text-gray-400">Manage active conversations and take over when needed</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate({ to: "/crm/start-conversation" })}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <span>💬</span>
                Start Conversation
              </button>
              <button
                onClick={() => navigate({ to: "/crm/campaigns" })}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <span>🚀</span>
                Start Campaign
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-4 flex-wrap">
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background   dark:text-white"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background   dark:text-white"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="human_takeover">Needs Attention</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
          </select>

          <select
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background   dark:text-white"
          >
            <option value="">All Platforms</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="messenger">Messenger</option>
          </select>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-background  rounded-lg p-4 border border-border dark:border-gray-700">
            <div className="text-sm  dark:text-gray-400">Active</div>
            <div className="text-2xl font-semibold text-green-600">
              {conversationsData?.conversations?.filter((c) => c.status === "active").length || 0}
            </div>
          </div>
          <div className="bg-background  rounded-lg p-4 border border-border dark:border-gray-700">
            <div className="text-sm  dark:text-gray-400">Needs Attention</div>
            <div className="text-2xl font-semibold text-orange-600">
              {conversationsData?.conversations?.filter((c) => c.humanTakeover).length || 0}
            </div>
          </div>
          <div className="bg-background  rounded-lg p-4 border border-border dark:border-gray-700">
            <div className="text-sm  dark:text-gray-400">Paused</div>
            <div className="text-2xl font-semibold text-yellow-600">
              {conversationsData?.conversations?.filter((c) => c.status === "paused").length || 0}
            </div>
          </div>
          <div className="bg-background  rounded-lg p-4 border border-border dark:border-gray-700">
            <div className="text-sm  dark:text-gray-400">Completed</div>
            <div className="text-2xl font-semibold ">
              {conversationsData?.conversations?.filter((c) => c.status === "completed").length || 0}
            </div>
          </div>
        </div>

        {/* Conversations List */}
        <div className="bg-background  rounded-lg shadow-sm border border-border dark:border-gray-700">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="text-gray-500">Loading conversations...</div>
            </div>
          ) : conversationsData?.conversations && conversationsData.conversations.length > 0 ? (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {conversationsData.conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={`p-6  dark:hover:bg-gray-700 cursor-pointer ${
                    conversation.humanTakeover ? "border-l-4 border-orange-500" : ""
                  }`}
                  onClick={() => navigate({ to: `/crm/inbox/${conversation.id}` })}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <PlatformIcon platform={conversation.platform} />
                        <h3 className="font-medium  dark:text-white">
                          {(conversation.contact as any)?.name ||
                            (conversation.contact as any)?.email ||
                            (conversation.contact as any)?.phone ||
                            "Unknown Contact"}
                        </h3>
                        <StatusBadge status={conversation.status} humanTakeover={conversation.humanTakeover} />
                        {conversation.priority >= 6 && (
                          <span className={`text-sm font-medium ${getPriorityColor(conversation.priority)}`}>
                            🔥 High Priority
                          </span>
                        )}
                      </div>

                      <div className="text-sm  dark:text-gray-400 mb-2">
                        {(conversation.campaign as any)?.name} •{" "}
                        {(conversation.currentStage as any)?.name || "No stage"}
                      </div>

                      <div className="text-sm text-gray-500 dark:text-gray-500 mb-3">
                        {getLastMessage(conversation as any)}
                      </div>

                      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                        <span>Last activity: {new Date(conversation.lastActivityAt).toLocaleDateString()}</span>
                        <span>Messages: {conversation.messages?.length || 0}</span>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      {conversation.status === "active" && !conversation.humanTakeover && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleStatusChange(conversation.id, "human_takeover", "Manual takeover from inbox")
                          }}
                          className="text-orange-600 hover:text-orange-800 text-sm px-3 py-1 border border-orange-600 rounded hover:bg-orange-50 dark:hover:bg-orange-900/20"
                        >
                          Take Over
                        </button>
                      )}

                      {conversation.humanTakeover && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleStatusChange(conversation.id, "active", "Released back to bot")
                          }}
                          className="text-green-600 hover:text-green-800 text-sm px-3 py-1 border border-green-600 rounded hover:bg-green-50 dark:hover:bg-green-900/20"
                        >
                          Release
                        </button>
                      )}

                      {conversation.status === "active" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleStatusChange(conversation.id, "paused", "Paused from inbox")
                          }}
                          className="text-yellow-600 hover:text-yellow-800 text-sm px-3 py-1 border border-yellow-600 rounded hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
                        >
                          Pause
                        </button>
                      )}

                      {conversation.status === "paused" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleStatusChange(conversation.id, "active", "Resumed from inbox")
                          }}
                          className="text-green-600 hover:text-green-800 text-sm px-3 py-1 border border-green-600 rounded hover:bg-green-50 dark:hover:bg-green-900/20"
                        >
                          Resume
                        </button>
                      )}

                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate({ to: `/crm/inbox/${conversation.id}` })
                        }}
                        className=" hover:text-blue-800 text-sm px-3 py-1 border border-blue-600 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="text-gray-500 mb-4">No conversations found</div>
              <div className="text-sm text-gray-400 mb-4">
                {statusFilter || platformFilter || searchTerm
                  ? "Try adjusting your filters or search terms"
                  : "Start conversations with your contacts"}
              </div>
              <div className="flex gap-2 justify-center">
                <button
                  onClick={() => navigate({ to: "/crm/start-conversation" })}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Start Conversation
                </button>
                <button
                  onClick={() => navigate({ to: "/crm/campaigns" })}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Go to Campaigns
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Bulk Actions (for future) */}
        {conversationsData?.conversations && conversationsData.conversations.length > 0 && (
          <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
            Showing {conversationsData.conversations.length} of {conversationsData.total} conversations
          </div>
        )}
      </div>
    </div>
  )
}

export default ConversationInbox
